/**
 * Environment Configuration
 * Following constitution requirements for environment-specific settings
 *
 * This file manages environment variables and provides fallback defaults
 * for different deployment environments (development, staging, production).
 */

/**
 * Load and validate environment variables
 * @returns {Object} Environment configuration object
 */
export function getEnvironmentConfig() {
  // API Configuration
  const apiConfig = {
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:80',
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 10000,
    retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS, 10) || 3,
    retryDelay: parseInt(process.env.REACT_APP_API_RETRY_DELAY, 10) || 1000,
    // Optional backend API key header used by the server for secure data collection
    secureApiKey: process.env.REACT_APP_DATA_COLLECTION_SECURE_API_KEY || process.env.REACT_APP_SECURE_API_KEY || ''
  };

  // Feature flags (Phase 1 vs Phase 2)
  const features = {
    // Phase 1 features (always enabled)
    authentication: true,
    subscriptionManagement: true,
    surebetDisplay: true,
    bookmakerFiltering: true,
    arbitrageCalculator: true,
    multiLanguage: true,

    // Phase 2 features (disabled in Phase 1)
    aiAnalysis: process.env.REACT_APP_ENABLE_AI_FEATURES === 'true' && false, // Force false for Phase 1
    aiRecommendations: false,
    enhancedPredictions: false,
    personalizedInsights: false,

    // Development features
    debugMode: process.env.REACT_APP_DEBUG_MODE === 'true',
    mockData: process.env.REACT_APP_USE_MOCK_DATA === 'true',
    performanceMonitoring: process.env.REACT_APP_PERFORMANCE_MONITORING !== 'false',

    // Telegram notification center (Phase 1)
    telegramNotifications: process.env.REACT_APP_FEATURE_TELEGRAM_NOTIFICATIONS === 'true',
  };

  // Debug configuration
  const debug = {
    enabled: process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_MODE === 'true',
    logLevel: process.env.REACT_APP_LOG_LEVEL || 'info',
    showErrorDetails: process.env.REACT_APP_SHOW_ERROR_DETAILS !== 'false',
    enableConsoleLogging: process.env.REACT_APP_CONSOLE_LOGGING !== 'false'
  };

  // Surebet-specific configuration (following constitution)
  const surebetConfig = {
    autoRefreshInterval: parseInt(process.env.REACT_APP_AUTO_REFRESH_INTERVAL, 10) || 600000, // 10 minutes
    minStakeAmount: parseFloat(process.env.REACT_APP_MIN_STAKE_AMOUNT) || 1.0,
    maxStakeAmount: parseFloat(process.env.REACT_APP_MAX_STAKE_AMOUNT) || 10000.0,
    profitThreshold: parseFloat(process.env.REACT_APP_PROFIT_THRESHOLD) || 0.01, // 1%
    staleDataWarning: parseInt(process.env.REACT_APP_STALE_DATA_WARNING, 10) || 900000, // 15 minutes
    maxDisplayedSurebets: parseInt(process.env.REACT_APP_MAX_DISPLAYED_SUREBETS, 10) || 500
  };

  // Security configuration
  const security = {
    enableCSRF: process.env.REACT_APP_ENABLE_CSRF !== 'false',
    enableHTTPS: process.env.REACT_APP_FORCE_HTTPS === 'true',
    tokenStorageType: process.env.REACT_APP_TOKEN_STORAGE || 'localStorage', // localStorage or sessionStorage
    maxLoginAttempts: parseInt(process.env.REACT_APP_MAX_LOGIN_ATTEMPTS, 10) || 5
  };

  return {
    api: apiConfig,
    features: features,
    debug: debug,
    surebet: surebetConfig,
    security: security,

    // Direct environment variable access
    nodeEnv: process.env.NODE_ENV || 'development',
    buildVersion: process.env.REACT_APP_BUILD_VERSION || '1.0.0',
    buildTimestamp: process.env.REACT_APP_BUILD_TIMESTAMP || new Date().toISOString()
  };
}

/**
 * Validate environment configuration
 * @param {Object} config - Environment configuration
 * @returns {Object} Validation result with errors
 */
export function validateEnvironmentConfig(config) {
  const errors = [];
  const warnings = [];

  // Validate API configuration
  if (!config.api.baseURL) {
    errors.push('API base URL is required');
  }

  if (config.api.timeout < 1000) {
    warnings.push('API timeout is very low, consider increasing to at least 5000ms');
  }

  // Validate surebet configuration
  if (config.surebet.minStakeAmount >= config.surebet.maxStakeAmount) {
    errors.push('Minimum stake amount must be less than maximum stake amount');
  }

  if (config.surebet.profitThreshold < 0) {
    errors.push('Profit threshold must be positive');
  }

  if (config.surebet.autoRefreshInterval < 60000) {
    warnings.push('Auto refresh interval is very low, consider increasing to at least 60 seconds');
  }

  // Validate security configuration
  if (config.security.maxLoginAttempts < 1) {
    errors.push('Maximum login attempts must be at least 1');
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
    warnings: warnings
  };
}

/**
 * Get environment-specific defaults
 * @param {string} environment - Environment name (development, staging, production)
 * @returns {Object} Environment-specific configuration
 */
export function getEnvironmentDefaults(environment) {
  const defaults = {
    development: {
      api: {
        baseURL: 'http://localhost:80',
        timeout: 30000
      },
      debug: {
        enabled: true,
        logLevel: 'debug',
        showErrorDetails: true
      },
      features: {
        debugMode: true,
        mockData: false
      }
    },

    staging: {
      api: {
        baseURL: 'https://staging-api.arbiwin.bg',
        timeout: 15000
      },
      debug: {
        enabled: true,
        logLevel: 'info',
        showErrorDetails: true
      },
      features: {
        debugMode: false,
        mockData: false
      }
    },

    production: {
      api: {
        baseURL: 'https://api.arbiwin.bg',
        timeout: 10000
      },
      debug: {
        enabled: false,
        logLevel: 'error',
        showErrorDetails: false
      },
      features: {
        debugMode: false,
        mockData: false
      }
    }
  };

  return defaults[environment] || defaults.development;
}

/**
 * Initialize environment configuration with validation
 * @returns {Object} Validated environment configuration
 */
export function initializeEnvironment() {
  const config = getEnvironmentConfig();
  const validation = validateEnvironmentConfig(config);

  if (!validation.isValid) {
    console.error('Environment configuration errors:', validation.errors);
    throw new Error('Invalid environment configuration: ' + validation.errors.join(', '));
  }

  if (validation.warnings.length > 0) {
    console.warn('Environment configuration warnings:', validation.warnings);
  }

  // Log configuration in development
  if (config.debug.enabled) {
    console.log('Environment configuration loaded:', {
      environment: config.nodeEnv,
      apiBaseURL: config.api.baseURL,
      featuresEnabled: Object.keys(config.features).filter(key => config.features[key]),
      buildVersion: config.buildVersion
    });
  }

  return config;
}

// Export environment configuration for immediate use
export const ENV_CONFIG = getEnvironmentConfig();

export default ENV_CONFIG;
